import React, { memo } from 'react';
import { Typography } from 'investira.react.components';

/**
 * Explica a leitura do retorno relativo apresentado no destaque.
 *
 * @returns {JSX.Element}
 */
export const AtivoHighlightRetornoTooltip = memo(() => {
  return (
    <React.Fragment>
      <Typography color="inherit" variant="caption" component="p" gutterBottom>
        <b>Retorno relativo</b>
      </Typography>
      <Typography color="inherit" variant="caption" component="p" gutterBottom>
        Mede a diferença, em pontos percentuais, entre a rentabilidade acumulada do ativo e a do
        benchmark nos últimos 12 meses.
      </Typography>
      <Typography color="inherit" variant="caption" component="p" sx={{ fontWeight: 500 }}>
        Valores de Referência:
      </Typography>
      <Typography color="inherit" variant="caption" component="p" gutterBottom>
        Mínimo: Um retorno abaixo de 0 indica que o ativo rendeu menos que o benchmark.
      </Typography>
      <Typography color="inherit" variant="caption" component="p" sx={{ fontWeight: 500 }}>
        Ideal:
      </Typography>
      <Typography color="inherit" variant="caption" component="p">
        {`Retorno = 0: O ativo ficou em linha com o benchmark.`}
      </Typography>
      <Typography color="inherit" variant="caption" component="p">
        {`Retorno > 0: O ativo superou o benchmark. Quanto maior, melhor.`}
      </Typography>
      <Typography color="inherit" variant="caption" component="p" gutterBottom>
        Máximo: Não há limite teórico, pois o resultado depende do desempenho do ativo e do
        benchmark no período.
      </Typography>
      <Typography color="inherit" variant="caption" component="p">
        O retorno relativo deve ser analisado com indicadores de risco e de consistência para
        contextualizar o desempenho do ativo.
      </Typography>
    </React.Fragment>
  );
});

AtivoHighlightRetornoTooltip.displayName = 'AtivoHighlightRetornoTooltip';

/**
 * Explica a leitura do índice de Sharpe apresentado no destaque.
 *
 * @returns {JSX.Element}
 */
export const AtivoHighlightSharpeTooltip = memo(() => {
  return (
    <React.Fragment>
      <Typography color="inherit" variant="caption" component="p" gutterBottom>
        <b>Sharpe</b>
      </Typography>
      <Typography color="inherit" variant="caption" component="p" gutterBottom>
        Mede o retorno excedente acima da taxa de juros livre de risco.
      </Typography>
      <Typography
        color="inherit"
        variant="caption"
        component="p"
        sx={{
          fontWeight: 500
        }}>
        Valores de Referência:
      </Typography>
      <Typography color="inherit" variant="caption" component="p" gutterBottom>
        Mínimo: Geralmente, um Sharpe abaixo de 0 é indesejável, pois indica que o investimento não
        compensa o risco em relação a taxa de juros livre de risco.
      </Typography>
      <Typography
        color="inherit"
        variant="caption"
        component="p"
        sx={{
          fontWeight: 500
        }}>
        Ideal:
      </Typography>
      <Typography
        color="inherit"
        variant="caption"
        component="p">{`Sharpe > 1: Considerado bom, indicando retorno positivo ajustado ao risco.`}</Typography>
      <Typography
        color="inherit"
        variant="caption"
        component="p">{`Sharpe > 2: Muito bom, refletindo eficiência significativa.`}</Typography>
      <Typography
        color="inherit"
        variant="caption"
        component="p">{`Sharpe > 3: Excelente, comum em carteiras altamente otimizadas.`}</Typography>
      <Typography color="inherit" variant="caption" component="p" gutterBottom>
        Máximo: Não há um limite teórico, mas valores acima de 3 são raros e podem indicar dados
        inconsistentes ou períodos específicos de mercado.
      </Typography>
      <Typography color="inherit" variant="caption" component="p">
        Um Sharpe maior implica melhor desempenho ajustado ao risco. Comparar com benchmarks de
        mercado (ex.: Sharpe de um índice como o S&P 500, que historicamente varia entre 0,5 e 1,5)
        ajuda a contextualizar.
      </Typography>
    </React.Fragment>
  );
});

AtivoHighlightSharpeTooltip.displayName = 'AtivoHighlightSharpeTooltip';
